import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '../lib/supabase';
import { toast } from 'react-toastify';
import { PropertyFormData } from '../types/property';
import { slugify } from '../utils/slugify';
import { normalizeEditorPrice } from '../utils/editorPrice';
import { normalizePropertyFeaturePayload } from '../utils/propertyFormFeatures';

export const usePropertyForm = (id: string | undefined) => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    pageViews: 0,
    contacts: 0,
    favorites: 0
  });

  const formMethods = useForm<PropertyFormData>({
    defaultValues: {
      title: '',
      description: '',
      reference_number: '',
      energy_class: undefined, // Fix: era '' ma deve essere undefined o un valore valido
      heating_type: '',
      extra_costs: '',
      total_rooms: undefined, // Fix: era '' ma deve essere number | undefined
      type_id: '',
      state_id: '',
      type: 'vendita',
      price: null,
      price_on_request: false,
      location: '',
      bedrooms: 0, // Fix: era '' ma deve essere number
      bathrooms: 0, // Fix: era '' ma deve essere number
      size: 0, // Fix: era '' ma deve essere number
      province_id: '',
      zone_id: '',
      status_id: '',
      featured: false,
      publish_immobiliare: false,
      publish_wikicasa: false,
      publish_idealista: false,
      features: [],
      strengths: [],
      priority: 0,
      latitude: undefined, // Fix: era null ma deve essere number | undefined
      longitude: undefined // Fix: era null ma deve essere number | undefined
    }
  });

  const { setValue, watch } = formMethods; // Rimuovi formState: { errors = {} }
  const title = watch('title');
  const location = watch('location');

  const generateUniqueReferenceNumber = async (baseRef: string): Promise<string> => {
    let counter = 1;
    let referenceNumber = baseRef;
    
    while (true) {
      const { data, error } = await supabase
        .from('properties')
        .select('id')
        .eq('reference_number', referenceNumber)
        .single();

      if (error || !data) {
        // Reference number is unique
        return referenceNumber;
      }

      // If this is an update operation and the found property is the current one, the reference is valid
      if (id && data.id === id) {
        return referenceNumber;
      }

      // Add counter to reference number
      referenceNumber = `${baseRef}-${counter}`;
      counter++;

      // Prevent infinite loop
      if (counter > 100) {
        throw new Error('Non è stato possibile generare un numero di riferimento unico');
      }
    }
  };

  const fetchPropertyStats = async (propertyId: string) => {
    try {
      // Fetch page views
      const { count: viewsCount } = await supabase
        .from('property_views')
        .select('*', { count: 'exact', head: true })
        .eq('property_id', propertyId);

      // Fetch contacts
      const { count: contactsCount } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
        .eq('property_id', propertyId);

      // Fetch favorites
      const { count: favoritesCount } = await supabase
        .from('property_favorites')
        .select('*', { count: 'exact', head: true })
        .eq('property_id', propertyId);

      setStats({
        pageViews: viewsCount || 0,
        contacts: contactsCount || 0,
        favorites: favoritesCount || 0
      });
    } catch (error) {
      console.error('Error fetching property stats:', error);
    }
  };

  const fetchProperty = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);

    try {
      // First fetch the property data
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select(`
          *,
          zones(*),
          provinces(*),
          property_types(*),
          property_states(*),
          property_statuses(*),
          property_features(
            feature_id
          ),
          property_strengths(*)
        `)
        .eq('id', id)
        .single();

      if (propertyError) throw propertyError;

      if (property) {
        // Il prezzo riservato non è nella tabella pubblica: la migrazione lo
        // conserva separatamente e lo rende leggibile solo agli amministratori.
        let editorPrice = property.price;
        if (property.price_on_request) {
          const { data: privatePrice, error: priceError } = await supabase
            .from('property_private_prices')
            .select('price')
            .eq('property_id', id)
            .maybeSingle();
          if (priceError) throw priceError;
          editorPrice = privatePrice?.price ?? null;
        }
        setValue('price', editorPrice);
        setValue('price_on_request', property.price_on_request ?? false);
        // Un record con stato nullo deve svuotare l'eventuale stato del record precedente.
        setValue('state_id', property.state_id ?? '');

        // Set form values from property data
        Object.entries(property).forEach(([key, value]) => {
          if (key !== 'price' && value !== null && key in formMethods.getValues()) {
            setValue(key as keyof PropertyFormData, value as any); // Fix: aggiunto type assertion
          }
        });

        // Set features
        if (property.property_features && property.property_features.length > 0) {
          const featureIds = property.property_features.map((pf: any) => pf.feature_id);
          setValue('features', featureIds);
        }

        // Set strengths
        if (property.property_strengths && property.property_strengths.length > 0) {
          const strengths = property.property_strengths.map((ps: any) => ({
            description: ps.description,
            order_index: ps.order_index
          }));
          setValue('strengths', strengths);
        }

        // Fetch property stats
        await fetchPropertyStats(id);
      }
    } catch (error: any) {
      console.error('Error fetching property:', error);
      setError(error.message);
      toast.error('Errore nel caricamento dell\'immobile');
    } finally {
      setLoading(false);
    }
  }, [id, setValue]);

  React.useEffect(() => {
    if (id) {
      fetchProperty();
    } else {
      // Set default draft status for new properties
      const setDefaultStatus = async () => {
        const { data: draftStatus } = await supabase
          .from('property_statuses')
          .select('id')
          .eq('name', 'draft')
          .single();
        
        if (draftStatus) {
          setValue('status_id', draftStatus.id);
        }
      };
      setDefaultStatus();
    }
  }, [id, setValue, fetchProperty]);

  const resetForm = useCallback(() => {
    formMethods.reset();
  }, [formMethods]);

  const generateSlug = () => {
    if (!title || !location) return '';
    const baseSlug = `${slugify(title)}-${slugify(location)}`;
    return baseSlug;
  };

  // Funzione per aggiornare il feed XML dopo il salvataggio di un immobile
  const updateXmlFeed = async () => {
    try {
      console.log('Aggiornamento feed XML dopo il salvataggio dell\'immobile...');

      // Verifica se l'immobile è pubblicato
      const { data: publishedStatus } = await supabase
        .from('property_statuses')
        .select('id')
        .eq('name', 'published')
        .single();
      
      if (!publishedStatus) return;
      
      const status_id = formMethods.getValues('status_id');
      
      // Genera il feed XML solo se l'immobile è pubblicato
      if (status_id === publishedStatus.id) {
        console.log('Immobile pubblicato, avvio rigenerazione XML...');
        
        // Chiama la Netlify Function per generare l'XML
        try {
          const timestamp = Date.now();
          const response = await fetch(`/.netlify/functions/property-feed?t=${timestamp}&force=true`, {
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
          
          if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
          }

          const xmlContent = await response.text();
          
          // Salva il file XML fisicamente
          try {
            const generateResponse = await fetch(`/.netlify/functions/generate-xml-file`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache, no-store, must-revalidate'
              },
              body: JSON.stringify({ xml: xmlContent })
            });
            
            if (!generateResponse.ok) {
              console.warn('Failed to save XML file', await generateResponse.text());
            }
          } catch (saveError) {
            console.warn('Error saving XML file:', saveError);
          }
          
          // Aggiorna anche il file pubblico XML per forzare l'aggiornamento della cache
          await fetch(`/.netlify/functions/property-feed?t=${timestamp}`, {
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
          
          console.log('XML feed regenerated successfully via Netlify function');
          
          // Aggiorna le impostazioni
          await supabase
            .from('settings')
            .upsert({
              id: '00000000-0000-0000-0000-000000000000',
              xml_feed_last_update: new Date().toISOString(),
              xml_feed_status: 'Tracciato XML aggiornato automaticamente dopo modifica immobile'
            })
          
          toast.success('Feed XML aggiornato con successo');
        } catch (netError) {
          console.error('Errore nella chiamata alla Netlify function:', netError);
          throw new Error('Impossibile generare il feed XML');
        }
      } else {
        console.log('Immobile non pubblicato, aggiornamento XML saltato');
      }
    } catch (error) {
      console.error('Error updating XML feed:', error);
      toast.error('Errore nell\'aggiornamento del feed XML');
    }
  };

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    setSubmitLoading(true);
    setError(null);

    try {
      // Generate or validate reference number
      let referenceNumber = data.reference_number;
      if (!referenceNumber) {
        // Generate a base reference number from the current date
        const date = new Date();
        referenceNumber = `PROP-${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
      
      // Ensure reference number is unique
      const uniqueReferenceNumber = await generateUniqueReferenceNumber(referenceNumber);

      // Sanitize numeric fields
      // Aggiungi questa funzione helper all'inizio del file
      const sanitizeNumber = (value: any, defaultValue: number = 0): number => {
        if (value === undefined || value === null || value === '' || value === 0) {
          return defaultValue;
        }
        return typeof value === 'string' ? parseInt(value) || defaultValue : value;
      };
      
      // Il catalogo live ha un solo terreno: il suo ID stabile permette la
      // validazione anche senza un'ulteriore SELECT a ogni salvataggio.
      const applicableFeatures = normalizePropertyFeaturePayload(data, { id: data.type_id });

      // Poi usa nei sanitizedData:
      const sanitizedData = {
        ...data,
        reference_number: uniqueReferenceNumber,
        price: normalizeEditorPrice(data.price, Boolean(data.price_on_request)),
        price_on_request: Boolean(data.price_on_request),
        bedrooms: sanitizeNumber(data.bedrooms, 0),
        ...applicableFeatures,
        size: typeof data.size === 'string' ? parseFloat(data.size) : data.size,
        total_rooms: data.total_rooms
          ? (typeof data.total_rooms === 'string' ? parseInt(data.total_rooms) : data.total_rooms)
          : null,
        priority: data.priority
          ? (typeof data.priority === 'string' ? parseInt(data.priority) : data.priority)
          : 0,
        latitude: data.latitude !== undefined && data.latitude !== null ? 
          (typeof data.latitude === 'string' ? parseFloat(data.latitude) : data.latitude) : null,
        longitude: data.longitude !== undefined && data.longitude !== null ? 
          (typeof data.longitude === 'string' ? parseFloat(data.longitude) : data.longitude) : null,
        slug: generateSlug()
      };

      // Remove relationship fields
      const propertyData = { ...sanitizedData };
      delete (propertyData as any).property_images;
      delete (propertyData as any).property_features;
      delete (propertyData as any).property_strengths;
      delete (propertyData as any).zones;
      delete (propertyData as any).provinces;
      delete (propertyData as any).property_types;
      delete (propertyData as any).property_states;
      delete (propertyData as any).property_statuses;
      delete (propertyData as any).strengths;
      delete (propertyData as any).features;

      if (id) {
        // Update property
        const { error: propertyError } = await supabase
          .from('properties')
          .update(propertyData)
          .eq('id', id);

        if (propertyError) throw propertyError;

        // Update features
        if (Array.isArray(data.features)) {
          // Delete existing features
          const { error: deleteFeaturesError } = await supabase
            .from('property_features')
            .delete()
            .eq('property_id', id);

          if (deleteFeaturesError) throw deleteFeaturesError;

          // Insert new features
          if (data.features.length > 0) {
            const featureRecords = data.features.map(featureId => ({
              property_id: id,
              feature_id: featureId
            }));

            const { error: insertFeaturesError } = await supabase
              .from('property_features')
              .insert(featureRecords);

            if (insertFeaturesError) throw insertFeaturesError;
          }
        }

        // Update strengths
        if (data.strengths && data.strengths.length >= 0) {
          // Delete existing strengths
          const { error: deleteStrengthsError } = await supabase
            .from('property_strengths')
            .delete()
            .eq('property_id', id);

          if (deleteStrengthsError) throw deleteStrengthsError;

          // Insert new strengths
          if (data.strengths && data.strengths.length > 0) {
            // Linea ~400 - Fix parametri strength e index
            const newStrengthRecords = data.strengths.map((strength: any, index: number) => ({
              property_id: id, // FIX: use existing property id in update branch
              description: strength.description,
              order_index: index
            }));
            
            // E aggiorna il riferimento nella insert:
            const { error: strengthsError } = await supabase
              .from('property_strengths')
              .insert(newStrengthRecords); // Usa il nuovo nome

            if (strengthsError) throw strengthsError;
          }
        }

        // Aggiorna il feed XML
        await updateXmlFeed();

        toast.success('Immobile aggiornato con successo');
        window.location.href = `/admin/properties/${id}/edit?updated=true`;
      } else {
        // Create new property
        const { data: newProperty, error: propertyError } = await supabase
          .from('properties')
          .insert(propertyData)
          .select()
          .single();

        if (propertyError) throw propertyError;

        if (newProperty) {
          // Insert features if any are selected
          if (Array.isArray(data.features) && data.features.length > 0) {
            const featureRecords = data.features.map(featureId => ({
              property_id: newProperty.id,
              feature_id: featureId
            }));

            const { error: featuresError } = await supabase
              .from('property_features')
              .insert(featureRecords);

            if (featuresError) throw featuresError;
          }

          // Insert strengths
          if (data.strengths && data.strengths.length > 0) {
            // Linea ~400 - Fix parametri strength e index
            const newStrengthRecords = data.strengths.map((strength: any, index: number) => ({
              property_id: newProperty.id,
              description: strength.description,
              order_index: index
            }));
            
            // E aggiorna il riferimento nella insert:
            const { error: strengthsError } = await supabase
              .from('property_strengths')
              .insert(newStrengthRecords); // Usa il nuovo nome

            if (strengthsError) throw strengthsError;
          }

          // Aggiorna il feed XML
          await updateXmlFeed();

          toast.success('Immobile creato con successo');
          window.location.href = `/admin/properties/${newProperty.id}/edit?created=true`;
        }
      }
    } catch (error: any) {
      console.error('Error saving property:', error);
      const errorMessage = error.message || 'Si è verificato un errore durante il salvataggio';
      setError(errorMessage);
      toast.error(`Errore: ${errorMessage}`);
    } finally {
      setSubmitLoading(false);
    }
  });

  return {
    loading,
    submitLoading,
    error,
    handleSubmit,
    formMethods,
    resetForm,
    formState: formMethods.formState,
    stats
  };
};